import { DepressionContentPage } from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";

interface LocalPageProps {
  slug: string;
  cityName: string;
  county: string;
  distance: string;
  title: string;
  description: string;
  keywords: string;
  localContext: string;
}

export const buildLocalPage = (props: LocalPageProps) => {
  const Component = () => {
    const related: RelatedLink[] = [
      { title: "TMS in Coral Springs", href: "/tms-coral-springs-fl" },
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      { title: "Traveling for Treatment", href: "/traveling-for-treatment" },
      {
        title: "Depression Treatment Cost",
        href: "/depression-treatment-cost",
      },
    ];

    return (
      <DepressionContentPage
        seo={{
          title: `${props.title} | Neuro Wellness TMS Centers of America`,
          description: props.description,
          canonical: `/${props.slug}`,
          keywords: props.keywords,
        }}
        badge={`${props.cityName}, FL`}
        heroTitle={props.title}
        heroSubtitle={`Serving patients from ${props.cityName} at our Coral Springs treatment center. Neuro Wellness TMS Centers of America offers TMS, SAINT®, SPRAVATO®, and ketamine treatment for depression, located approximately ${props.distance} from ${props.cityName} in ${props.county}.`}
        sections={[
          {
            heading: `Serving ${props.cityName} From Coral Springs`,
            variant: "text",
            body: <p>{props.localContext}</p>,
          },
          {
            heading: "Why Patients Travel to Our Coral Springs Center",
            variant: "text",
            body: (
              <p>
                Patients from {props.cityName} and throughout South Florida
                choose our center because of the range of treatment options we
                provide — including accelerated TMS protocols that aren't
                available at every clinic. Our team has experience with
                fMRI-guided targeting and multi-session-per-day scheduling that
                a standard TMS clinic may not offer.
              </p>
            ),
          },
          {
            heading: "Treatments Available",
            variant: "cards",
            cards: [
              {
                title: "Traditional TMS",
                body: "FDA-cleared magnetic stimulation for treatment-resistant depression.",
              },
              {
                title: "SAINT® Accelerated TMS",
                body: "fMRI-guided, five-day accelerated TMS protocol.",
              },
              {
                title: "SPRAVATO® (Esketamine)",
                body: "FDA-approved nasal spray for treatment-resistant depression.",
              },
              {
                title: "IV Ketamine",
                body: "IV ketamine infusions and ketamine-assisted psychotherapy.",
              },
            ],
          },
          {
            heading: "Getting Here From " + props.cityName,
            variant: "text",
            body: (
              <p>
                Our Coral Springs treatment center is approximately{" "}
                {props.distance} from {props.cityName}. We can provide
                directions and travel guidance when you schedule your
                consultation. For patients traveling from farther away, we can
                discuss accommodation arrangements.
              </p>
            ),
          },
          {
            heading: "Scheduling a Consultation",
            variant: "callout",
            callout: `You don't need to know which treatment you need before contacting us. Start with a consultation — we'll help you understand what options may be appropriate for your situation.`,
          },
        ]}
        faq={[
          {
            question: `Do you have an office in ${props.cityName}?`,
            answer: `No. Our treatment center is located in Coral Springs, Florida. We serve patients from ${props.cityName} and throughout South Florida from our Coral Springs location.`,
          },
          {
            question: `How far is your center from ${props.cityName}?`,
            answer: `Our Coral Springs center is approximately ${props.distance} from ${props.cityName}. We can provide specific directions when you schedule your visit.`,
          },
          {
            question: "Do you treat patients from outside Coral Springs?",
            answer:
              "Yes. We serve patients from throughout South Florida and beyond. Many of our patients travel from out of state for treatment.",
          },
        ]}
        relatedLinks={related}
        finalCtaText={`Serving patients from ${props.cityName} at our Coral Springs treatment center. Call our team to schedule a consultation.`}
      />
    );
  };
  return Component;
};
